import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config'

const VerifyEmail: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { token } = useParams();
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const verifyEmail = useCallback(async () => {
    if (!token || verificationAttempted) return;

    try {
      setVerificationAttempted(true);
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-email/${token}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage('Your email has been successfully verified!');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              verificationSuccess: true 
            }
          });
        }, 2000);
      } else {
        setStatus('error');
        setMessage(data.error || 'Verification failed. Please try again.');
        // If the token is expired and we got the email in the response
        if (data.error?.toLowerCase().includes('expired') && data.email) {
          setUserEmail(data.email);
        }
      }
    } catch (err) {
      setStatus('error');
      setMessage('An error occurred during verification. Please try again.');
    }
  }, [token, navigate, verificationAttempted]);

  useEffect(() => {
    verifyEmail();
  }, [verifyEmail]);

  const handleReturnToLogin = () => {
    navigate('/login', {
      state: {
        verificationExpired: true,
        userEmail: userEmail
      }
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {status === 'loading' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Verifying your email...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="rounded-full h-12 w-12 bg-green-100 flex items-center justify-center mx-auto">
              <svg className="h-6 w-6 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">{message}</h2>
            <p className="mt-2 text-gray-600">Redirecting to login page...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto">
              <svg className="h-6 w-6 text-red-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-gray-900">Verification Failed</h2>
            <p className="mt-2 text-gray-600">{message}</p>
            <button
              onClick={handleReturnToLogin}
              className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Return to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;